import React from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { esPregunta } from '../../../lib/formularios';
import GraficaPregunta from './GraficaPregunta';
import { estiloTooltip, paletaDe, useColoresGrafica } from './colores';
import './respuestas.css';

/*
 * Resumen de respuestas: estadísticas del cuestionario y una tarjeta por pregunta con su gráfica.
 * Lo usan la pestaña Respuestas del editor y quien responde con `?resumen=1` (`publico`: el
 * servidor ya quita archivos y correos; aquí solo se ocultan las listas de archivos).
 */

const redondear = (n) => (n === null || n === undefined ? '—' : Math.round(n * 100) / 100);

function Estadistica({ etiqueta, valor, de }) {
  return (
    <div className="rs-estadistica">
      <div className="rs-estadistica-valor">
        {valor}
        {de !== undefined && de !== null && <span className="rs-estadistica-de"> / {de} puntos</span>}
      </div>
      <div className="rs-estadistica-etiqueta">{etiqueta}</div>
    </div>
  );
}

export function EstadisticasCuestionario({ cuestionario, colorTema }) {
  const colores = useColoresGrafica();
  if (!cuestionario) return null;
  const c = cuestionario;
  const color = paletaDe(colorTema)[0];
  const distribucion = (c.distribucion || []).map((d) => ({ puntos: String(d.puntos), n: d.n }));
  return (
    <section className="rs-tarjeta" aria-labelledby="rs-cuestionario-titulo">
      <h3 className="rs-tarjeta-titulo" id="rs-cuestionario-titulo">Estadísticas</h3>
      <div className="rs-estadisticas">
        <Estadistica etiqueta="Media" valor={redondear(c.media)} de={c.maximo} />
        <Estadistica etiqueta="Mediana" valor={redondear(c.mediana)} de={c.maximo} />
        <Estadistica etiqueta="Rango" valor={`${redondear(c.minimo)} – ${redondear(c.maximo_obtenido)}`} de={c.maximo} />
      </div>
      {distribucion.length > 0 && (
        <>
          <div className="rs-subtitulo">Puntos totales distribuidos</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={distribucion} margin={{ top: 20, right: 8, bottom: 16, left: -18 }}>
              <CartesianGrid vertical={false} stroke={colores.linea} />
              <XAxis
                dataKey="puntos"
                tick={{ fill: colores.texto2, fontSize: 12 }}
                stroke={colores.linea2}
                label={{ value: 'Puntos obtenidos', position: 'insideBottom', offset: -8, fill: colores.texto3, fontSize: 12 }}
              />
              <YAxis allowDecimals={false} tick={{ fill: colores.texto2, fontSize: 12 }} stroke={colores.linea2} />
              <Tooltip {...estiloTooltip(colores)} formatter={(n) => [n, 'Personas']} labelFormatter={(p) => `${p} puntos`} />
              <Bar dataKey="n" fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={48}>
                <LabelList dataKey="n" position="top" fill={colores.texto2} fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
      {c.falladas?.length > 0 && (
        <>
          <div className="rs-subtitulo">Preguntas que se fallan con frecuencia</div>
          <ul className="rs-falladas">
            {c.falladas.map((f) => (
              <li key={f.item_id} className="rs-fallada">
                <span className="rs-fallada-titulo">{f.titulo || 'Pregunta sin título'}</span>
                <span className="rs-fallada-n">{f.correctas} de {f.total} respuestas correctas</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default function Resumen({ formulario, resumen, publico = false }) {
  if (!resumen) return null;
  const colorTema = formulario?.tema?.color;
  const preguntas = (formulario?.items || []).filter(esPregunta);
  const esCuestionario = !!formulario?.ajustes?.es_cuestionario;

  return (
    <div className="rs-resumen">
      {!publico && <EstadisticasCuestionario cuestionario={resumen.cuestionario} colorTema={colorTema} />}
      {preguntas.map((item) => {
        const datos = resumen.preguntas?.[item.id];
        const respondidas = datos?.respondidas || 0;
        return (
          <section key={item.id} className="rs-tarjeta" aria-labelledby={`rs-q-${item.id}`}>
            <h3 className="rs-tarjeta-titulo" id={`rs-q-${item.id}`}>{item.titulo || 'Pregunta sin título'}</h3>
            <div className="rs-tarjeta-meta">
              {respondidas} {respondidas === 1 ? 'respuesta' : 'respuestas'}
              {esCuestionario && !publico && datos && datos.correctas !== undefined && datos.correctas !== null && (
                <span className="rs-chip-correctas">{datos.correctas} de {respondidas} correctas</span>
              )}
            </div>
            <GraficaPregunta item={item} datos={datos} colorTema={colorTema} publico={publico} />
          </section>
        );
      })}
      {!preguntas.length && <p className="rs-vacio">Este formulario todavía no tiene preguntas.</p>}
    </div>
  );
}
